// Local-development stubs for the Automation Assistant insights page.
// Scratch file for manual testing — not intended to be committed.
//
// Built to QA `errorRateEstimate: null` — the backend now reports "no
// documents were confirmed that day" as a null error rate instead of implying
// a zero one, and the charts have to render that as a gap rather than as 100%
// precision.
//
// Enable in the browser console, then reload:
//
//   localStorage.setItem('mock_server_enabled', '1');
//   localStorage.setItem('mock_server_scenario', 'automationInsights');
//
// Scenarios (see mocks/browser.ts):
//   automationInsights         8 weeks of data with null days: weekends, one
//                              fully idle week, and the last two days (no data
//                              yet). Switch the aggregation period to compare
//                              Days / Weeks / Months.
//   automationInsightsAllNull  every day null — precision chart draws nothing
//                              and the header average falls back to `-`
//   automationInsightsNoGaps   an estimate on every day — the pre-change
//                              baseline, for spotting regressions
//
// Turn off with:
//
//   localStorage.removeItem('mock_server_enabled');
//
// NOTE: the page only renders when `mat-317-automation-settings-v2` is ON. The
// queue handler below also forces `quality_spot_check_percentage` above zero,
// otherwise the precision chart is replaced by PrecisionDisabledBanner.

import { bypass, http, HttpResponse, passthrough } from 'msw';

// Matches the develop backend (https://elis.develop.r8.lol/api/v1/...) and a
// local proxy (/api/v1/...) alike, with or without a query string. Any queue
// id, so switching queues keeps returning data.
const AUTOMATION_INSIGHTS_URL =
  /\/api\/v1\/queues\/\d+\/automation\/insights(?:\?.*)?$/;
const AUTOMATION_TARGETS_URL =
  /\/api\/v1\/queues\/\d+\/automation\/targets(?:\?.*)?$/;
const QUEUE_URL = /\/api\/v1\/queues\/\d+(?:\?.*)?$/;

// The response is snake_case on the wire — the api-client camelCases it and
// then validates against `automationInsightsSchema`, so every field below is
// required and the shape has to match exactly.
type EstimatedErrorRateTimeseriesBody = {
  date: string;
  // null = no documents were confirmed in this window, so there is no estimate
  error_rate_estimate: number | null;
  is_quality_estimate: boolean;
  window_document_count: number;
};

type DocumentAutomationTimeseriesBody = {
  date: string;
  automated_count: number;
  non_automated_count: number;
  touched_count: number;
  touchless_count: number;
};

type DocumentBlockerBody = {
  blocker: string;
  document_count: number;
  granularity: 'annotation' | 'datapoint';
  example_annotation_ids: number[];
};

type DatapointStatisticsBody = {
  schema_id: string;
  // deprecated and keyed by blocker name — kept empty so the api-client's
  // camelCase pass has no snake_case record keys to mangle
  blocked_document_counts: Record<string, never>;
  estimated_error_rate: number | null;
  confidence_threshold: number | null;
  blockers: DocumentBlockerBody[];
  is_quality_estimate: boolean;
};

type AutomationInsightsBody = {
  document_automation_rate: number;
  document_automation_timeseries: DocumentAutomationTimeseriesBody[];
  datapoint_statistics: DatapointStatisticsBody[];
  estimated_error_rate: number | null;
  document_blockers: DocumentBlockerBody[];
  is_aurora_queue: boolean;
  estimated_error_rate_timeseries: EstimatedErrorRateTimeseriesBody[];
};

// Eight weeks, ending today, so weekly and monthly aggregation both have
// several buckets to fold and the x-axis shows recognisable recent dates.
const DAY_COUNT = 56;

// The last two days have not been processed yet. This is the gap the original
// report was about: it used to plot as 100% precision.
const TRAILING_GAP = 2;

// Estimates start out rough and become quality estimates once the spot check
// has collected enough samples — exercises both scatter series and the
// "rough estimate" legend entry.
const ROUGH_ESTIMATE_DAYS = 14;

const dateAt = (index: number) => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - (DAY_COUNT - 1 - index));
  return date;
};

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

// A whole Monday-to-Sunday week without a single document, in the middle of
// the range — the case where an *aggregated* week has nothing to average and
// must come out as null rather than 0. Anchored to a Monday so the gap lines
// up with one bucket in the Weeks view instead of straddling two.
const IDLE_WEEK_START =
  Array.from({ length: 7 }, (_, offset) => 24 + offset).find(
    index => dateAt(index).getUTCDay() === 1
  ) ?? 24;

// Deterministic, so two reloads produce the same chart and a QA note about
// "the third bar" keeps meaning something.
const wobble = (index: number, modulo: number) => (index * 13) % modulo;

const hasDocuments = (index: number) => {
  const weekday = dateAt(index).getUTCDay();
  const isWeekend = weekday === 0 || weekday === 6;
  const isIdleWeek = index >= IDLE_WEEK_START && index < IDLE_WEEK_START + 7;
  const isTrailingGap = index >= DAY_COUNT - TRAILING_GAP;

  return !isWeekend && !isIdleWeek && !isTrailingGap;
};

const documentCounts = (index: number) => {
  if (!hasDocuments(index)) {
    return { automated: 0, nonAutomated: 0 };
  }

  return {
    automated: 40 + wobble(index, 23),
    nonAutomated: 12 + wobble(index, 11),
  };
};

// Days with no documents are still present in the series, with zero counts —
// the "pad missing data with zeros" half of the same change.
const documentAutomationTimeseries = (): DocumentAutomationTimeseriesBody[] =>
  Array.from({ length: DAY_COUNT }, (_, index) => {
    const { automated, nonAutomated } = documentCounts(index);

    return {
      date: toIsoDate(dateAt(index)),
      automated_count: automated,
      non_automated_count: nonAutomated,
      touched_count: nonAutomated,
      touchless_count: automated,
    };
  });

const estimatedErrorRateTimeseries = ({
  forceNull = false,
  forceEstimates = false,
}: {
  // every day null, whether or not it had documents
  forceNull?: boolean;
  // never null, i.e. the shape the frontend received before this change
  forceEstimates?: boolean;
} = {}): EstimatedErrorRateTimeseriesBody[] =>
  Array.from({ length: DAY_COUNT }, (_, index) => {
    const { automated, nonAutomated } = documentCounts(index);
    const documentCount = automated + nonAutomated;
    const hasEstimate = !forceNull && (forceEstimates || documentCount > 0);

    return {
      date: toIsoDate(dateAt(index)),
      error_rate_estimate: hasEstimate
        ? Number((0.02 + wobble(index, 9) / 100).toFixed(4))
        : null,
      is_quality_estimate: index >= ROUGH_ESTIMATE_DAYS,
      window_document_count: documentCount,
    };
  });

const DOCUMENT_BLOCKERS: DocumentBlockerBody[] = [
  {
    blocker: 'low_score',
    document_count: 198,
    granularity: 'datapoint',
    example_annotation_ids: [7100001, 7100002],
  },
  {
    blocker: 'failed_checks',
    document_count: 151,
    granularity: 'datapoint',
    example_annotation_ids: [7100003],
  },
  {
    blocker: 'human_confirmation_required',
    document_count: 96,
    granularity: 'annotation',
    example_annotation_ids: [7100004],
  },
  {
    blocker: 'automation_disabled',
    document_count: 37,
    granularity: 'annotation',
    example_annotation_ids: [],
  },
];

// A handful of rows so the insights table is not in its empty state while the
// charts above it are being QA'd. Schema ids that the loaded queue does not
// have simply render as the raw id.
const DATAPOINT_STATISTICS: DatapointStatisticsBody[] = [
  {
    schema_id: 'document_id',
    blocked_document_counts: {},
    estimated_error_rate: 0.031,
    confidence_threshold: 0.94,
    blockers: [DOCUMENT_BLOCKERS[0]!],
    is_quality_estimate: true,
  },
  {
    schema_id: 'date_due',
    blocked_document_counts: {},
    estimated_error_rate: 0.087,
    confidence_threshold: 0.88,
    blockers: [DOCUMENT_BLOCKERS[1]!],
    is_quality_estimate: false,
  },
  {
    schema_id: 'amount_total',
    blocked_document_counts: {},
    // no estimate for this field at all — the table's own null branch
    estimated_error_rate: null,
    confidence_threshold: null,
    blockers: [],
    is_quality_estimate: false,
  },
];

export const automationInsightsResponse = (
  overrides?: Partial<AutomationInsightsBody>
): AutomationInsightsBody => ({
  document_automation_rate: 0.71,
  document_automation_timeseries: documentAutomationTimeseries(),
  datapoint_statistics: DATAPOINT_STATISTICS,
  estimated_error_rate: 0.058,
  document_blockers: DOCUMENT_BLOCKERS,
  is_aurora_queue: true,
  estimated_error_rate_timeseries: estimatedErrorRateTimeseries(),
  ...overrides,
});

// Two saved targets, so the precision target line has a visible step inside
// the charted range.
const automationTargetsResponse = () => ({
  pagination: { total: 2, next: null, previous: null, total_pages: 1 },
  results: [
    {
      automation_rate_target: 0.6,
      error_rate_target: 0.08,
      datapoint_automation_targets: [],
      type: 'automation_assistant_v1',
      datetime: `${toIsoDate(dateAt(0))}T00:00:00.000000Z`,
    },
    {
      automation_rate_target: 0.75,
      error_rate_target: 0.04,
      datapoint_automation_targets: [],
      type: 'automation_assistant_v1',
      datetime: `${toIsoDate(dateAt(30))}T00:00:00.000000Z`,
    },
  ],
});

// Everything else reaches the real backend (including the schema itself), and
// stays out of the "unhandled request" warnings.
const passthroughEverythingElse = http.all('*', () => passthrough());

const insightsHandler = (overrides?: Partial<AutomationInsightsBody>) =>
  http.get(AUTOMATION_INSIGHTS_URL, () =>
    HttpResponse.json(automationInsightsResponse(overrides))
  );

const targetsHandler = () =>
  http.get(AUTOMATION_TARGETS_URL, () =>
    HttpResponse.json(automationTargetsResponse())
  );

// Keeps the real queue, only lifting the quality spot check above zero so the
// precision chart renders instead of PrecisionDisabledBanner.
const qualitySpotCheckHandler = () =>
  http.get(QUEUE_URL, async ({ request }) => {
    const originalResponse = await (await fetch(bypass(request))).json();

    return HttpResponse.json({
      ...originalResponse,
      quality_spot_check_percentage: 0.02,
    });
  });

const scenario = (overrides?: Partial<AutomationInsightsBody>) => [
  insightsHandler(overrides),
  targetsHandler(),
  qualitySpotCheckHandler(),
  passthroughEverythingElse,
];

export const automationInsightsHandlers = scenario();

export const automationInsightsAllNullHandlers = scenario({
  estimated_error_rate: null,
  estimated_error_rate_timeseries: estimatedErrorRateTimeseries({
    forceNull: true,
  }),
});

export const automationInsightsNoGapsHandlers = scenario({
  estimated_error_rate_timeseries: estimatedErrorRateTimeseries({
    forceEstimates: true,
  }),
});
